import type { ReactNode } from 'react';

import type { TimelineFocusSectionInput } from './loggerContext';
import type { useTimelineLogger } from './useTimelineLogger';
import type { useInWorkoutTargets } from './useInWorkoutTargets';
import type { useEffortCommit } from './useEffortCommit';
import type { cueForEntry } from './usePlanCues';
import { FocusPanel } from './FocusPanel';
import type { FocusPanelProps } from './FocusPanelProps';
import { focusTargetStrip } from './FocusPanelTargetStrip';
import { EmptyFocusCard } from './EmptyFocusCard';
import { renderOrder } from './loggerUnits';
import { RosterStrip } from './RosterStrip';
import { buildTimerStartHandlers } from './timerStartHandlers';
import { focusCardPin } from './pinnedAdvance';
import type { PinnedExercise } from './usePinnedExercise';

type TimelineLogger = ReturnType<typeof useTimelineLogger>;

// The top of the timeline logger: the roster strip and the FocusPanel — i.e.
// the "pick an exercise" + "the single data-entry point" regions of the three
// the screen doc-comment describes. Split out of TimelineLoggerScreen when the
// shell sat at exactly 500 lines with zero margin (CN-27), following the mould
// TimelineFeedSection already set for the bottom region: a pure render shell
// that reads the same context bag the screen does, owns no state, and receives
// the already-derived values (targets, effort bag, plan cues) as data.
export function TimelineFocusSection({
  ctx,
  tl,
  targetRows,
  effort,
  activeCues,
  allLogged,
  prescribeAxis,
  prescribeLoad,
  weekCue,
  onToggleTimed,
  timedActive,
  noteActive,
  prescribeNoteEditor,
  prescribeEntryNoteEditor,
  hideRoster = false,
  prescribeUnplaced,
  prescribeLocked,
  pendingSlot,
  pin,
  onMoreTime,
}: {
  ctx: TimelineFocusSectionInput;
  tl: TimelineLogger;
  targetRows: ReturnType<typeof useInWorkoutTargets>;
  effort: ReturnType<typeof useEffortCommit>;
  /** The active entry's coach cues, already resolved by `cueForEntry` —
   *  ⛔ not `map.get(slug)`, which since LG-34 hands back EVERY occurrence. */
  activeCues: ReturnType<typeof cueForEntry>;
  /** Every exercise fully logged — drives the empty-slot placeholder copy. */
  allLogged: boolean;
  /** PE-22 K3.2 — prescribe-only slot, same shape as `targetStrip`: the host
   *  builds the element or omits it; this
   *  shell never branches on mode to decide whether to show it. Undefined in
   *  'log'/'log-past'. */
  prescribeAxis?: ReactNode;
  /** PE-34 — prescribe only, pure pass-through to the card's weight field:
   *  there it authors the PRESCRIBED load instead of recording a lift. */
  prescribeLoad?: FocusPanelProps['prescribeLoad'];
  /** PE-31, prescribe only: the week cursor + its step, drawn by the card's
   *  first row. Pure pass-through (same shape as `prescribeAxis`): this shell
   *  never branches on mode to decide whether the week exists. */
  weekCue?: {
    index: number;
    count: number;
    onNext: () => void;
    onPrev: () => void;
  };
  /** PE-22 K3.4 — "A tempo" chip data: undefined ⇒ FocusPanelChipRow hides
   *  the chip (presence-is-visibility, same convention as the two slots
   *  above). */
  onToggleTimed?: () => void;
  timedActive?: boolean;
  /** PE-22 K3.4 — "Nota" chip's data-driven selected state (has the row a
   *  note?) plus its editor slot, same generic-slot shape as `prescribeAxis`.
   *  The chip's OPEN/CLOSE is local FocusPanel UI state (like Burn+'s bar),
   *  not threaded through here — FocusPanel hands its own close handler to
   *  the render function so the editor's explicit close button can reuse it. */
  noteActive?: boolean;
  prescribeNoteEditor?: (onClose: () => void) => ReactNode;
  /** PE-37 — the per-EXERCISE note's editor slot; see FocusPanelProps. */
  prescribeEntryNoteEditor?: (onClose: () => void) => ReactNode;
  /** PE-27 fetta 2 — the host draws the roster ITSELF, above the tab switcher,
   *  so this section must not draw a second one. Same convention as
   *  FocusPanel's `hideFail`/`hideBurnPlus`: default false ⇒ the
   *  logger is untouched. ⛔ It hides the STRIP, never the ＋ it carries —
   *  whoever passes this is responsible for mounting one, which is why the
   *  prescribe tree renders the real RosterStrip rather than a copy. */
  hideRoster?: boolean;
  /** PE-27 fetta 2 — what to show INSTEAD of the "pick an exercise" card when
   *  an exercise IS focused but is not placed in the week on screen. The host
   *  builds the node (it owns the week and the placement toggle); this shell
   *  does not branch on why the card is empty. Undefined ⇒ today's behaviour.
   */
  prescribeUnplaced?: ReactNode;
  /** #336 Q8: what to show INSTEAD of the card when the focused exercise is
   *  read only in this editor. The host builds it. Undefined: the card. */
  prescribeLocked?: ReactNode;
  /** AP-9b: the unstarted «today» draft card. While present it IS the
   *  section, roster included: a focus set by any path (a roster tap, an add)
   *  must not put a FocusPanel in its place and make the session look
   *  started (AP-9c). Undefined: today's behaviour. */
  pendingSlot?: ReactNode;
  /** #308: the Pin. Live logging only; absent ⇒ no switch, no roster glyph. */
  pin?: PinnedExercise;
  /** #356: «More time» beside the roster's ＋ (useMoreTime). Absent: none. */
  onMoreTime?: () => void;
}) {
  const {
    draft,
    dispatch,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    groupLabelFor,
    groupIndexFor,
    defaultUnit,
    setPickerOpen,
    setEffortScaleOpen,
    setTipEntryIdx,
    setSwapEntryIdx,
    setGroupPickerEntryIdx,
    setSupersetMenu,
    plate,
    hold,
    timerDefaults,
    mode,
  } = ctx;

  const {
    resolvedActiveId,
    activeEntry,
    activeEntryIdx,
    boundSet,
    boundSetIdx,
    activeExercise,
    canRemove,
    effortScale,
    groupMembers,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
    focusRow,
    confirmSplitGroup,
    onRemoveExercise,
    onChangeSet,
    onAddSet,
    onRemoveSet,
    onInsertSetBefore,
    onToggleLoadedSides,
    cueKey,
    flashCue,
  } = tl;

  return pendingSlot ? (
    pendingSlot
  ) : draft.entries.length === 0 ? (
    // Blank ad-hoc workout: empty card explains how to begin (Option B).
    // PE-24 — prescribe authoring reuses this same empty slot (no slots on
    // this day yet); it keeps its own message because it is AUTHORING, not
    // logging, but the ＋ is the SAME one, for the same reason as the roster's
    // (PE-29, below): on an empty day it is the only way in.
    <EmptyFocusCard
      message={
        mode === 'prescribe'
          ? t('composer.prescribeDayEmpty', {
              defaultValue: 'No exercises on this day yet.',
            })
          : t('mobile.logger.empty')
      }
      action={{
        label: t('mobile.logger.addExercise'),
        onPress: () => setPickerOpen(true),
      }}
      theme={theme}
    />
  ) : (
    <>
      {/* Roster hybrid (slice 4) — switch which exercise FocusPanel
          targets; each chip shows state + logged/planned count + group
          marker so the queue reads at a glance (v2.67/v2.68 smoke).
          ⭐ COLLAPSED at start since PE-31 (it used to open expanded, as a
          self-teaching first screen); from there the chevron is the only thing
          that opens it. See useLoggerCursor. */}
      {hideRoster ? null : (
        // ⭐ LG-45 — the roster draws `renderOrder`, not the raw array. Three
        // surfaces used to read three different orders: the roster drew
        // `draft.entries`, the save iterated `renderOrder`, so an exercise
        // added mid-session and put in a superset showed A B C D and saved
        // A B D C. His decision (7/09): `renderOrder` is the truth, and the
        // roster reads it — what you looked at is what gets written.
        // ⛔ Not the modals below (LoggerScreen passes them the raw array on
        // purpose): they address entries by `entryIdx`, so for them the index
        // IS the address and a reordered array would point at another row.
        <RosterStrip
          entries={renderOrder(draft.entries)}
          activeEntryId={resolvedActiveId}
          onFocus={focusRow}
          resolveName={getExerciseNameBySlug}
          groupLabelFor={groupLabelFor}
          groupIndexFor={groupIndexFor}
          expanded={rosterExpanded}
          onToggleExpanded={() => setRosterExpanded((v) => !v)}
          onAddExercise={() => setPickerOpen(true)}
          {...(onMoreTime ? { onMoreTime } : {})}
          pinnedId={pin?.pinnedId ?? null}
          t={t}
          theme={theme}
        />
      )}

      {activeEntry && prescribeLocked ? (
        prescribeLocked
      ) : activeEntry ? (
        <FocusPanel
          title={getExerciseNameBySlug(
            activeEntry.exerciseId,
            activeEntry.exerciseName,
          )}
          entry={activeEntry}
          set={boundSet}
          activeSetIdx={boundSetIdx >= 0 ? boundSetIdx : null}
          planPerSetNotes={activeCues?.perSetNotes}
          // PE-37 — the coach's cue when a session is RUNNING (activeCues
          // resolves against a started instance), the slot's own note when the
          // card is authoring one: in prescribe there is no instance, so
          // without the fallback the note would be written and never shown.
          planEntryNote={activeCues?.instruction ?? activeEntry.notePrescribed}
          canRemove={canRemove}
          exercise={activeExercise}
          defaultUnit={defaultUnit}
          effortScale={effortScale}
          onChange={onChangeSet}
          onSelectSet={setSelectedSetIdx}
          onAddSet={onAddSet}
          onRemoveSet={onRemoveSet}
          onInsertSetBefore={onInsertSetBefore}
          onToggleUnit={() =>
            dispatch({
              type: 'toggle_unit',
              entryIdx: activeEntryIdx,
              setIdx: boundSetIdx,
            })
          }
          onToggleLoadedSides={onToggleLoadedSides}
          onToggleAmrap={() => {
            if (boundSetIdx < 0 || !boundSet) return;
            dispatch({
              type: 'update_set',
              entryIdx: activeEntryIdx,
              setIdx: boundSetIdx,
              // In LOG this is the OUTCOME. `amrapPrescribed` is not in the
              // update_set patch key union at all, so no UI path can rewrite
              // the plan's request from here.
              // ⭐ PE-42 — in PRESCRIBE there is no outcome to record, so the
              // same key carries the coach's REQUEST and prescribeDispatch
              // writes it to CellDraft.amrap. The flip reads the field the
              // chip is showing, or the first tap would be a no-op.
              patch: {
                amrap:
                  mode === 'prescribe'
                    ? !boundSet.amrapPrescribed
                    : !boundSet.amrap,
              },
            });
          }}
          effortMeter={{
            value: effort.value,
            failed: effort.failed,
            onSelect: effort.onSelect,
            onFail: effort.onFail,
            flashRir: effort.flashRir,
            flashKey: effort.flashKey,
          }}
          // Start-timer affordance for any duration-logged entry —
          // cardio + timed holds (TM-5: cardio had none). Gating +
          // countdown-seed rule live in buildTimerStartHandlers.
          timer={buildTimerStartHandlers({
            entry: activeEntry,
            entryIdx: activeEntryIdx,
            boundSet,
            boundSetIdx,
            hold,
            onOpenDefaults: timerDefaults.open,
          })}
          // Edit-scope (slice 5): on a PAST-SESSION EDIT the structural
          // controls (＋ add set, create/split superset, add chain step)
          // are greyed + balloon-locked; allowed edits (values, remove,
          // fail-toggle, replace/remove exercise) stay live. This is the
          // single place the mode becomes `editLocked` — the ~30 reads
          // downstream are prop-drilling, not re-derivations.
          // ⚠️ PE-22 K0 — this asks 'log-past' EXACTLY, and the widening to
          // `mode !== 'log'` it replaces was the bug waiting for the third
          // mode: in PRESCRIPTION those same controls are not edits to
          // forbid, they ARE the authoring. Locking them would have made the
          // new editor unable to add a set. For 'log'/'log-past' nothing
          // changes.
          editLocked={mode === 'log-past'}
          // PE-22 K3.1 — prescribe authoring only: Fail and Burn+ are silent
          // no-ops there (no outcome to fail, no dropset/myorep chain to
          // run), so the card hides both rather than showing a control that
          // does nothing. 'log'/'log-past' are untouched.
          hideFail={mode === 'prescribe'}
          hideBurnPlus={mode === 'prescribe'}
          // PE-35: a program slot has nowhere to store the BI/UNI count
          // (loadedSides), so the chip could only write a value the save path
          // drops. Which side is SlotDraft.side (#75), not this chip.
          hideLaterality={mode === 'prescribe'}
          // PE-42 — the chip states the REQUEST here, not the outcome.
          amrapPrescribing={mode === 'prescribe'}
          prescribeAxis={prescribeAxis}
          prescribeLoad={prescribeLoad}
          weekCue={weekCue}
          pin={focusCardPin(pin, resolvedActiveId, draft.entries)}
          onToggleTimed={onToggleTimed}
          timedActive={timedActive}
          noteActive={noteActive}
          prescribeNoteEditor={prescribeNoteEditor}
          prescribeEntryNoteEditor={prescribeEntryNoteEditor}
          onOpenPlates={() => plate.openForSet(activeEntryIdx, boundSetIdx)}
          onOpenEffortScale={() => setEffortScaleOpen(true)}
          onSwap={() => setSwapEntryIdx(activeEntryIdx)}
          onSplitGroup={
            activeEntry.group
              ? () => confirmSplitGroup(activeEntry.group!.id)
              : undefined
          }
          historyOpen={activeEntry.historyOpen}
          onToggleHistory={() =>
            dispatch({
              type: 'toggle_entry_history',
              entryIdx: activeEntryIdx,
              next: !activeEntry.historyOpen,
            })
          }
          onOpenTip={() => setTipEntryIdx(activeEntryIdx)}
          onRemoveExercise={onRemoveExercise}
          // The superset control on row 2 (#308): S+ standalone, N/M + ☰.
          groupMembers={groupMembers}
          groupLabel={
            activeEntry.group ? groupLabelFor(activeEntry.group) : ''
          }
          activeEntryId={resolvedActiveId}
          resolveName={getExerciseNameBySlug}
          onSelectMember={focusRow}
          onCreateGroup={
            // S+ ALWAYS opens the picker on the SUPERSET tab with THIS
            // exercise already in (AP-0k2), even with one exercise: the
            // tab's catalog list supplies the partner.
            activeEntry.group === null
              ? () => setGroupPickerEntryIdx(activeEntryIdx)
              : undefined
          }
          cueKey={cueKey}
          flashCue={flashCue}
          targetStrip={focusTargetStrip({
            rows: targetRows,
            units: defaultUnit,
            locale: i18n.language,
            resolveExerciseName: (slug) => getExerciseNameBySlug(slug, slug),
            theme,
          })}
          onOpenGroupMenu={
            activeEntry.group
              ? () =>
                  setSupersetMenu({
                    groupId: activeEntry.group!.id,
                    entryIdx: activeEntryIdx,
                  })
              : undefined
          }
          confirm={ctx.deps.confirm}
          theme={theme}
        />
      ) : prescribeUnplaced ? (
        // PE-27 fetta 2 — an exercise IS focused, it just is not placed in
        // the week on screen. Before this the card fell through to the
        // "select an exercise above" copy while a chip sat selected right
        // above it: the words denied what the screen showed.
        prescribeUnplaced
      ) : (
        // No exercise focused. Two cases: every set is logged (the
        // workout just completed — point A end-state) → "all logged, add
        // another or save"; otherwise workout start / after a remove →
        // "pick above or add a new one".
        <EmptyFocusCard
          message={
            // PE-24 — in prescribe there is no "logged" end-state, so the
            // all-logged copy is never honest here; the roster above (＋
            // included, since PE-29) is where you pick or add.
            mode === 'prescribe'
              ? t('composer.prescribeFocusEmpty', {
                  defaultValue:
                    'Select an exercise above to prescribe its sets.',
                })
              : allLogged
                ? t('mobile.timeline.focusAllLogged', {
                    defaultValue:
                      'All sets logged. Add an exercise above, or save below.',
                  })
                : t('mobile.timeline.focusEmptyHasRoster', {
                    defaultValue:
                      'Select an exercise above to start, or add a new one.',
                  })
          }
          flashOnAppear={mode !== 'prescribe' && allLogged}
          theme={theme}
        />
      )}
    </>
  );
}
